"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  knowledgeBasesListKnowledgeBases,
  knowledgeBasesGetKnowledgeBase,
  knowledgeBasesCreateKnowledgeBase,
  knowledgeBasesUpdateKnowledgeBase,
  knowledgeBasesDeleteKnowledgeBase,
  knowledgeBasesListFolders,
  knowledgeBasesCreateFolder,
  knowledgeBasesUpdateFolder,
  knowledgeBasesDeleteFolder,
  knowledgeBasesListFiles,
  knowledgeBasesGetFile,
  knowledgeBasesMoveFile,
  knowledgeBasesDeleteFile,
} from "../sdk";
import type {
  KbSummary,
  KbDetail,
  CreateKbRequest,
  UpdateKbRequest,
  FolderSummary,
  FolderTree,
  FolderListResponse,
  CreateFolderRequest,
  UpdateFolderRequest,
  KbFileSummary,
  KbFileResponse,
  MoveFileRequest,
} from "../sdk";
import { queryKeys } from "../query/keys";
import {
  LOW_CHURN_STALE_TIME,
  MEDIUM_CHURN_STALE_TIME,
  GC_TIME,
} from "../query/config";

// Re-export SDK types for consumers of @wayak/ai/queries.
export type {
  KbSummary,
  KbDetail,
  CreateKbRequest,
  UpdateKbRequest,
  FolderSummary,
  FolderTree,
  FolderListResponse,
  CreateFolderRequest,
  UpdateFolderRequest,
  KbFileSummary,
  KbFileResponse,
  MoveFileRequest,
};

// ---------------------------------------------------------------------------
// Knowledge Base queries
// ---------------------------------------------------------------------------

/**
 * Fetch knowledge bases for an organization.
 */
export function useKnowledgeBases(options: {
  orgId: string;
  enabled?: boolean;
}) {
  const { orgId, enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.knowledgeBases.list({ orgId }),
    queryFn: async () => {
      const response = await knowledgeBasesListKnowledgeBases({
        path: { org_id: orgId },
      });
      return response.data ?? [];
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: enabled && !!orgId,
  });
}

/**
 * Fetch a single knowledge base with stats.
 */
export function useKnowledgeBase(options: {
  orgId: string;
  kbId: string | undefined;
  enabled?: boolean;
}) {
  const { orgId, kbId, enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.knowledgeBases.detail(kbId ?? ""),
    queryFn: async () => {
      if (!kbId) return null;
      const response = await knowledgeBasesGetKnowledgeBase({
        path: { org_id: orgId, kb_id: kbId },
      });
      return response.data ?? null;
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: enabled && !!orgId && !!kbId,
  });
}

/**
 * Create a new knowledge base.
 *
 * Adds to list cache and sets detail cache on success.
 */
export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string;
      data: CreateKbRequest;
    }) => {
      const response = await knowledgeBasesCreateKnowledgeBase({
        path: { org_id: orgId },
        body: data,
      });
      if (!response.data) throw new Error("Failed to create knowledge base");
      return response.data;
    },
    onSuccess: (newKB, variables) => {
      // Add to list cache
      queryClient.setQueryData<KbSummary[]>(
        queryKeys.knowledgeBases.list({ orgId: variables.orgId }),
        (old) => [newKB, ...(old ?? [])]
      );
      // Set detail cache
      queryClient.setQueryData(
        queryKeys.knowledgeBases.detail(newKB.id),
        newKB
      );
    },
  });
}

/**
 * Update a knowledge base.
 *
 * Updates both detail and list caches.
 */
export function useUpdateKnowledgeBase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      kbId,
      data,
    }: {
      orgId: string;
      kbId: string;
      data: UpdateKbRequest;
    }) => {
      const response = await knowledgeBasesUpdateKnowledgeBase({
        path: { org_id: orgId, kb_id: kbId },
        body: data,
      });
      if (!response.data) throw new Error("Failed to update knowledge base");
      return response.data;
    },
    onSuccess: (updatedKB, variables) => {
      // Update detail cache
      queryClient.setQueryData(
        queryKeys.knowledgeBases.detail(variables.kbId),
        updatedKB
      );
      // Update in list cache
      queryClient.setQueryData<KbSummary[]>(
        queryKeys.knowledgeBases.list({ orgId: variables.orgId }),
        (old) =>
          (old ?? []).map((kb) =>
            kb.id === variables.kbId ? { ...kb, ...updatedKB } : kb
          )
      );
    },
  });
}

/**
 * Delete a knowledge base.
 *
 * Optimistically removes from cache, rolls back on error.
 */
export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      kbId,
    }: {
      orgId: string;
      kbId: string;
    }) => {
      await knowledgeBasesDeleteKnowledgeBase({
        path: { org_id: orgId, kb_id: kbId },
      });
      return kbId;
    },
    onMutate: async ({ orgId, kbId }) => {
      const listKey = queryKeys.knowledgeBases.list({ orgId });

      await queryClient.cancelQueries({ queryKey: listKey });

      const previous = queryClient.getQueryData<KbSummary[]>(listKey);

      queryClient.setQueryData<KbSummary[]>(listKey, (old) =>
        (old ?? []).filter((kb) => kb.id !== kbId)
      );

      queryClient.removeQueries({
        queryKey: queryKeys.knowledgeBases.detail(kbId),
      });

      return { previous, listKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.listKey, context.previous);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Folder queries
// ---------------------------------------------------------------------------

/**
 * Fetch folders for a knowledge base (flat list + tree).
 */
export function useKBFolders(options: {
  orgId: string;
  kbId: string | undefined;
  enabled?: boolean;
}) {
  const { orgId, kbId, enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.kbFolders.list(kbId ?? ""),
    queryFn: async () => {
      if (!kbId) return { folders: [] as FolderSummary[], tree: [] as FolderTree[] };
      const response = await knowledgeBasesListFolders({
        path: { org_id: orgId, kb_id: kbId },
      });
      return response.data ?? { folders: [] as FolderSummary[], tree: [] as FolderTree[] };
    },
    staleTime: LOW_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: enabled && !!orgId && !!kbId,
  });
}

/**
 * Create a folder.
 *
 * Invalidates folder list/tree on success.
 */
export function useCreateKBFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      kbId,
      data,
    }: {
      orgId: string;
      kbId: string;
      data: CreateFolderRequest;
    }) => {
      const response = await knowledgeBasesCreateFolder({
        path: { org_id: orgId, kb_id: kbId },
        body: data,
      });
      if (!response.data) throw new Error("Failed to create folder");
      return response.data;
    },
    onSuccess: (_newFolder, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kbFolders.list(variables.kbId),
      });
    },
  });
}

/**
 * Update a folder.
 *
 * Invalidates folder list/tree on success.
 */
export function useUpdateKBFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      kbId,
      folderId,
      data,
    }: {
      orgId: string;
      kbId: string;
      folderId: string;
      data: UpdateFolderRequest;
    }) => {
      const response = await knowledgeBasesUpdateFolder({
        path: { org_id: orgId, kb_id: kbId, folder_id: folderId },
        body: data,
      });
      if (!response.data) throw new Error("Failed to update folder");
      return response.data;
    },
    onSuccess: (_updatedFolder, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kbFolders.list(variables.kbId),
      });
    },
  });
}

/**
 * Delete a folder.
 *
 * Optimistically removes from cache, rolls back on error.
 * Also invalidates file lists since files may shift to root.
 */
export function useDeleteKBFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      kbId,
      folderId,
    }: {
      orgId: string;
      kbId: string;
      folderId: string;
    }) => {
      await knowledgeBasesDeleteFolder({
        path: { org_id: orgId, kb_id: kbId, folder_id: folderId },
      });
      return folderId;
    },
    onSuccess: (_deletedId, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kbFolders.list(variables.kbId),
      });
      // Files may have shifted to root after folder deletion
      queryClient.invalidateQueries({
        queryKey: queryKeys.kbFiles.lists(),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// File queries
// ---------------------------------------------------------------------------

const TERMINAL_STATUSES = new Set(["completed", "error", "partial"]);

/**
 * Fetch files for a knowledge base, optionally filtered by folder.
 *
 * Smart polling: polls every 5s when any file is still processing.
 */
export function useKBFiles(options: {
  orgId: string;
  kbId: string | undefined;
  folderId?: string | null;
  enabled?: boolean;
}) {
  const { orgId, kbId, folderId, enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.kbFiles.list({ kbId: kbId ?? "", folderId }),
    queryFn: async () => {
      if (!kbId) return [];
      const response = await knowledgeBasesListFiles({
        path: { org_id: orgId, kb_id: kbId },
        query: folderId !== undefined ? { folder_id: folderId } : undefined,
      });
      return response.data ?? [];
    },
    staleTime: MEDIUM_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: enabled && !!orgId && !!kbId,
    refetchInterval: (query) => {
      const files = query.state.data;
      if (!files || files.length === 0) return false;
      const hasProcessing = files.some(
        (f) => !TERMINAL_STATUSES.has(f.processing_status)
      );
      return hasProcessing ? 5000 : false;
    },
  });
}

/**
 * Fetch a single KB file by ID, including processing status and
 * uploader metadata. Polls every 5s while the file is still processing.
 */
export function useKBFile(options: {
  orgId: string;
  kbId: string | undefined;
  fileId: string | undefined;
  enabled?: boolean;
}) {
  const { orgId, kbId, fileId, enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.kbFiles.detail(fileId ?? ""),
    queryFn: async () => {
      if (!kbId || !fileId) return null;
      const response = await knowledgeBasesGetFile({
        path: { org_id: orgId, kb_id: kbId, file_id: fileId },
      });
      return response.data ?? null;
    },
    staleTime: MEDIUM_CHURN_STALE_TIME,
    gcTime: GC_TIME,
    enabled: enabled && !!orgId && !!kbId && !!fileId,
    refetchInterval: (query) => {
      const file = query.state.data;
      if (!file) return false;
      return TERMINAL_STATUSES.has(file.processing_status) ? false : 5000;
    },
  });
}

/**
 * Move a file to a different folder.
 *
 * Invalidates file lists on success.
 */
export function useMoveKBFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      kbId,
      fileId,
      targetFolderId,
    }: {
      orgId: string;
      kbId: string;
      fileId: string;
      targetFolderId: string | null;
    }) => {
      const response = await knowledgeBasesMoveFile({
        path: { org_id: orgId, kb_id: kbId, file_id: fileId },
        body: { folder_id: targetFolderId },
      });
      if (!response.data) throw new Error("Failed to move file");
      return response.data;
    },
    onSuccess: (_movedFile, variables) => {
      // Invalidate all file lists for this KB (source and destination folders)
      queryClient.invalidateQueries({
        queryKey: queryKeys.kbFiles.lists(),
      });
      // Also invalidate KB detail (file counts may change per folder)
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledgeBases.detail(variables.kbId),
      });
      // Org-wide KB list surfaces per-folder counts on the index tile.
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledgeBases.lists(),
      });
    },
  });
}

/**
 * Delete a file.
 *
 * Optimistically removes from cache, rolls back on error.
 */
export function useDeleteKBFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orgId,
      kbId,
      fileId,
      folderId,
    }: {
      orgId: string;
      kbId: string;
      fileId: string;
      folderId?: string | null;
    }) => {
      await knowledgeBasesDeleteFile({
        path: { org_id: orgId, kb_id: kbId, file_id: fileId },
      });
      return fileId;
    },
    onMutate: async ({ kbId, fileId, folderId }) => {
      const listKey = queryKeys.kbFiles.list({ kbId, folderId });

      await queryClient.cancelQueries({ queryKey: listKey });

      const previous = queryClient.getQueryData<KbFileSummary[]>(listKey);

      queryClient.setQueryData<KbFileSummary[]>(listKey, (old) =>
        (old ?? []).filter((f) => f.id !== fileId)
      );

      return { previous, listKey };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.listKey, context.previous);
      }
    },
    onSuccess: (_deletedId, variables) => {
      // Invalidate KB detail (file count changed)
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledgeBases.detail(variables.kbId),
      });
      // Org-wide KB list shows file_count / size on each tile; without
      // this the index page stats stay stale until refresh.
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledgeBases.lists(),
      });
    },
  });
}
